import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Topbar } from "../ui/Topbar";
import { useAlerts, useAlertsStatus, type AlertRuleStatus } from "../features/alerts/useAlerts";
import { AddAlertModal } from "../features/alerts/AddAlertModal";
import { fmtNum } from "../lib/format";
import { useScrollRestore } from "../lib/useScrollRestore";

type Bucket = "near" | "mid" | "far";

function bucketOf(distance: number | null | undefined): Bucket | "unknown" {
  if (distance == null) return "unknown";
  const a = Math.abs(distance);
  if (a < 2) return "near";
  if (a < 5) return "mid";
  return "far";
}

function distLabel(d: number | null | undefined): {
  text: string;
  color: string;
  weight: number;
} {
  if (d == null) return { text: "—", color: "var(--ink-soft)", weight: 400 };
  const a = Math.abs(d);
  if (a < 0.5)
    return { text: "вот-вот", color: "#c0392b", weight: 600 };
  if (a < 2)
    return { text: `${d.toFixed(2)} %`, color: "var(--accent)", weight: 500 };
  if (a < 5)
    return { text: `${d.toFixed(2)} %`, color: "var(--ink-2)", weight: 400 };
  return { text: `${d.toFixed(1)} %`, color: "var(--ink-soft)", weight: 400 };
}

export default function AlertsPage() {
  const status = useAlertsStatus();
  const { removeAlert } = useAlerts();
  const scrollRef = useScrollRestore<HTMLDivElement>("alerts");
  const [tab, setTab] = useState<Bucket>("near");
  const [editing, setEditing] = useState<AlertRuleStatus | null>(null);

  const permission =
    typeof Notification !== "undefined" ? Notification.permission : "default";

  const all = status.data ?? [];
  const buckets = useMemo(() => {
    const near: AlertRuleStatus[] = [];
    const mid: AlertRuleStatus[] = [];
    const far: AlertRuleStatus[] = [];
    for (const a of all) {
      const b = bucketOf(a.distance_pct);
      if (b === "near") near.push(a);
      else if (b === "mid") mid.push(a);
      else far.push(a); // unknown тоже сюда
    }
    const byDist = (xs: AlertRuleStatus[]) =>
      [...xs].sort((a, b) => {
        const ad = a.distance_pct == null ? Infinity : Math.abs(a.distance_pct);
        const bd = b.distance_pct == null ? Infinity : Math.abs(b.distance_pct);
        return ad - bd;
      });
    return { near: byDist(near), mid: byDist(mid), far: byDist(far) };
  }, [all]);

  const list =
    tab === "near" ? buckets.near : tab === "mid" ? buckets.mid : buckets.far;

  return (
    <>
      <Topbar
        title="Управление алертами"
        subtitle="отсортировано по близости к срабатыванию"
        right={
          <span className="cache-status">
            <span>{all.length} активных</span>
          </span>
        }
      />

      {permission !== "granted" && (
        <div className="banner" style={{ marginBottom: 14 }}>
          Браузер не разрешил нотификации. Алерты будут срабатывать в БД,
          но push в системе не покажется. При создании первого алерта появится
          запрос разрешения.
        </div>
      )}

      <div className="row" style={{ marginBottom: 14, gap: 8 }}>
        <div className="bucket-tabs">
          <button
            type="button"
            className={tab === "near" ? "on" : ""}
            onClick={() => setTab("near")}
          >
            Близко · {buckets.near.length}
          </button>
          <button
            type="button"
            className={tab === "mid" ? "on" : ""}
            onClick={() => setTab("mid")}
          >
            В зоне · {buckets.mid.length}
          </button>
          <button
            type="button"
            className={tab === "far" ? "on" : ""}
            onClick={() => setTab("far")}
          >
            Далеко · {buckets.far.length}
          </button>
        </div>
        <span className="soft" style={{ marginLeft: "auto", fontSize: 13 }}>
          отсортировано по близости
        </span>
      </div>

      <div ref={scrollRef} className="page-scroll">
        {status.isPending && <div className="banner">загружаю…</div>}

        {!status.isPending && all.length === 0 && (
          <div className="banner">
            нет активных алертов — открой карточку бумаги и нажми «+ алерт»
          </div>
        )}

        {all.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {list.length === 0 && (
              <div
                style={{
                  padding: "32px 20px",
                  textAlign: "center",
                  color: "var(--ink-soft)",
                  fontSize: 13,
                  fontStyle: "italic",
                }}
              >
                В этой зоне алертов нет
              </div>
            )}
            {list.map((a, i) => (
              <AlertRow
                key={a.rule.id}
                row={a}
                showBorder={i < list.length - 1}
                onEdit={() => setEditing(a)}
                onDelete={() => removeAlert(a.rule.id)}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <AddAlertModal
          secid={editing.rule.secid}
          secname={editing.secname ?? editing.rule.secid}
          currentPrice={editing.current_price ?? editing.rule.threshold}
          editRuleId={editing.rule.id}
          initialKind={editing.rule.kind}
          initialThreshold={editing.rule.threshold}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function AlertRow({
  row,
  showBorder,
  onEdit,
  onDelete,
}: {
  row: AlertRuleStatus;
  showBorder: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const dl = distLabel(row.distance_pct);
  const isHot = row.distance_pct != null && Math.abs(row.distance_pct) < 0.5;

  // Заполнение шкалы: 0% когда дистанция = ±10% или больше, 100% когда дистанция 0.
  const RANGE = 10;
  const fillPct =
    row.distance_pct == null
      ? 0
      : Math.max(0, Math.min(100, 100 - (Math.abs(row.distance_pct) / RANGE) * 100));

  const arrow = row.rule.kind === "price_below" ? "↓" : "↑";

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuPos({ x: r.right - 220, y: r.bottom + 4 });
  };

  return (
    <div
      className="alert-row"
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/bond/${row.rule.secid}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/bond/${row.rule.secid}`);
        }
      }}
      style={{
        borderBottom: showBorder ? "1px solid var(--line)" : "none",
      }}
    >
      <div className="alert-row-name">
        <div className="alert-row-secname" title={row.secname ?? row.rule.secid}>
          {row.secname ?? row.rule.secid}
        </div>
        <div className="mono soft" style={{ fontSize: 11 }}>
          {row.rule.secid}
        </div>
      </div>

      <div className="alert-row-bar-wrap">
        <div className="alert-row-bar-labels">
          <span style={{ color: "var(--accent)" }}>
            цель {arrow}{" "}
            <b className="mono">{fmtNum(row.rule.threshold)} ₽</b>
          </span>
          <span
            className="mono"
            style={{
              color: isHot ? "#c0392b" : "var(--ink-2)",
              fontWeight: isHot ? 600 : 400,
            }}
          >
            {dl.text} до срабатывания
          </span>
          <span>
            сейчас{" "}
            <b className="mono">
              {row.current_price == null ? "—" : `${fmtNum(row.current_price)} ₽`}
            </b>
          </span>
        </div>
        <div className="alert-row-bar">
          <div
            className={`alert-row-bar-fill${isHot ? " hot" : ""}`}
            style={{ width: `${fillPct}%` }}
          />
          <div className="alert-row-bar-target" />
          <div className="alert-row-bar-current" />
        </div>
      </div>

      <div className="alert-row-actions">
        <button
          type="button"
          className="btn sm"
          onClick={openMenu}
          aria-label="действия"
        >
          ⋯
        </button>
        {menuPos && (
          <RowMenu
            pos={menuPos}
            onClose={() => setMenuPos(null)}
            onEdit={() => {
              setMenuPos(null);
              onEdit();
            }}
            onGoto={() => {
              setMenuPos(null);
              navigate(`/bond/${row.rule.secid}`);
            }}
            onDelete={() => {
              setMenuPos(null);
              onDelete();
            }}
          />
        )}
      </div>
    </div>
  );
}

function RowMenu({
  pos,
  onClose,
  onEdit,
  onGoto,
  onDelete,
}: {
  pos: { x: number; y: number };
  onClose: () => void;
  onEdit: () => void;
  onGoto: () => void;
  onDelete: () => void;
}) {
  useEffect(() => {
    const onDoc = () => onClose();
    const onScroll = () => onClose();
    document.addEventListener("click", onDoc);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("click", onDoc);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);
  return (
    <div
      className="row-menu alert-row-menu"
      style={{ position: "fixed", left: pos.x, top: pos.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <button type="button" className="row-menu-item" onClick={onEdit}>
        <span className="row-menu-ic">✎</span>Изменить цель
      </button>
      <button type="button" className="row-menu-item" onClick={onGoto}>
        <span className="row-menu-ic">→</span>Перейти к бумаге
      </button>
      <div className="row-menu-sep" />
      <button
        type="button"
        className="row-menu-item danger"
        onClick={onDelete}
      >
        <span className="row-menu-ic">×</span>Удалить алерт
      </button>
    </div>
  );
}
